import React, { useState } from 'react';
import {
  Alert, Box, Button, DialogContent, DialogTitle, FormControl, FormLabel, Input,
  Modal, ModalClose, ModalDialog, Option, Select, Typography,
} from '@mui/joy';
import { Folder, Plus } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminSequencesAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { PlatformEmailTemplateFolder } from '../../../types/admin';

import { templateFoldersKey } from './useTemplateFolders';

export function TemplateFolderSelect({ folders, value, onChange, disabled, label = 'Folder' }: {
  folders: PlatformEmailTemplateFolder[];
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <Select size="sm" aria-label={label} value={value ?? 'unfiled'} disabled={disabled}
      startDecorator={<Folder size={16} />} sx={{ minWidth: 140, maxWidth: '100%' }}
      onChange={(_, id) => id && onChange(id === 'unfiled' ? null : id)}>
      <Option value="unfiled">Unfiled</Option>
      {folders.map(folder => <Option key={folder.id} value={folder.id}>{folder.name}</Option>)}
    </Select>
  );
}

export function TemplateFolders({ folders, selected, onSelect, counts }: {
  folders: PlatformEmailTemplateFolder[];
  selected: string;
  onSelect: (id: string) => void;
  counts: Record<string, number>;
}) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'create' | 'rename' | 'delete' | null>(null);
  const [name, setName] = useState('');
  const current = folders.find(folder => folder.id === selected);
  const mutation = useMutation({
    mutationFn: async () => {
      if (dialog === 'create') return AdminSequencesAPI.createTemplateFolder(name.trim());
      if (!current) throw new Error('Choose a folder first.');
      if (dialog === 'rename') return AdminSequencesAPI.renameTemplateFolder(current.id, name.trim());
      await AdminSequencesAPI.deleteTemplateFolder(current.id);
      return null;
    },
    onSuccess: async folder => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: templateFoldersKey }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'email-templates'] }),
      ]);
      onSelect(folder?.id ?? 'unfiled');
      setDialog(null);
    },
  });
  const open = (action: 'create' | 'rename' | 'delete') => {
    mutation.reset();
    setName(action === 'rename' ? current?.name ?? '' : '');
    setDialog(action);
  };
  const close = () => { if (!mutation.isPending) setDialog(null); };
  return <>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <FormControl sx={{ minWidth: 180, flex: { xs: '1 1 100%', sm: '0 1 280px' } }}>
        <FormLabel>Browse folders</FormLabel>
        <Select size="sm" value={selected} startDecorator={<Folder size={16} />}
          onChange={(_, id) => id && onSelect(id)}>
          <Option value="all">All templates ({counts.all ?? 0})</Option>
          <Option value="unfiled">Unfiled ({counts.unfiled ?? 0})</Option>
          {folders.map(folder => <Option key={folder.id} value={folder.id}>
            {folder.name} ({counts[folder.id] ?? 0})
          </Option>)}
        </Select>
      </FormControl>
      <Button size="sm" variant="outlined" startDecorator={<Plus size={16} />} onClick={() => open('create')}>New folder</Button>
      {current && <>
        <Button size="sm" variant="plain" color="neutral" onClick={() => open('rename')}>Rename folder</Button>
        <Button size="sm" variant="plain" color="danger" onClick={() => open('delete')}>Delete folder</Button>
      </>}
    </Box>
    <Typography level="body-xs" textColor="text.secondary">Use the folder selector beside a template to move it. Templates remain available to every sequence.</Typography>
    <Modal open={dialog !== null} onClose={close}>
      <ModalDialog sx={{ width: 440, maxWidth: 'calc(100vw - 24px)' }}>
        <ModalClose disabled={mutation.isPending} />
        <DialogTitle>{dialog === 'delete' ? 'Delete folder?' : dialog === 'rename' ? 'Rename folder' : 'New folder'}</DialogTitle>
        <DialogContent>{dialog === 'delete'
          ? `Templates in “${current?.name}” will move to Unfiled. No templates or sequence steps will be deleted.`
          : 'Give this group of templates a short, recognizable name.'}</DialogContent>
        <form onSubmit={event => { event.preventDefault(); mutation.mutate(); }}>
          {dialog !== 'delete' && <FormControl>
            <FormLabel>Folder name</FormLabel>
            <Input autoFocus required value={name} disabled={mutation.isPending}
              slotProps={{ input: { maxLength: 120 } }} onChange={event => setName(event.target.value)} />
          </FormControl>}
          {mutation.error && <Alert color="danger" sx={{ mt: 1 }}>{getApiErrorMessage(mutation.error, 'The folder could not be saved.')}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="plain" color="neutral" disabled={mutation.isPending} onClick={close}>Cancel</Button>
            <Button type="submit" color={dialog === 'delete' ? 'danger' : 'primary'} loading={mutation.isPending}
              disabled={dialog !== 'delete' && !name.trim()}>
              {dialog === 'delete' ? 'Delete folder' : dialog === 'rename' ? 'Save name' : 'Create folder'}
            </Button>
          </Box>
        </form>
      </ModalDialog>
    </Modal>
  </>;
}
